import React, { useEffect } from 'react';
import Card from '../../lib/card/Card';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAccessToken, getCurrentUserOrLogoutRedirect } from '../../auth';
import { CircularProgress, Box } from '@mui/material';
import { Logger } from '@elyx-code/common-ts-utils';
import { IProject_meta } from '@elyx-code/project-logic-tree';
import { envConfig } from '../../config';
import { IUser } from '@elyx-code/definitions';
import Popup from '../../lib/popup/Popup';
import { ProPlanChip } from '../ProPlanChip';
import { useWorkspaceStore } from '../../store/workspace';

const CreateNewProject: React.FC<{
	onCreated: (newProject: IProject_meta) => void;
	totalProjectCount: number;
	viewMode?: 'grid' | 'rows';
}> = ({ onCreated, totalProjectCount, viewMode = 'grid' }) => {
	const navigate = useNavigate();
	const [creating, setCreating] = React.useState(false);
	const [user, setUser] = React.useState<IUser>();

	const activeWorkspace = useWorkspaceStore((state) => state.activeWorkspace);

	// if (activeWorkspace?.limits?.maxProjects !== undefined) {
	// 	// for testing
	// 	activeWorkspace.limits.maxProjects = 1;
	// }

	const isAtProjectLimit =
		activeWorkspace?.limits?.maxProjects !== -1 &&
		activeWorkspace?.limits?.maxProjects <= totalProjectCount;

	const createApp = async (
		e: React.MouseEvent<HTMLDivElement, MouseEvent>
	) => {
		if (creating || totalProjectCount === -1) {
			return;
		}

		if (isAtProjectLimit) {
			navigate('/pricing/plans');
			return;
		}

		setCreating(true);

		const response = await axios.post(
			`${envConfig.API_BASE_URL}/editor-service/v1/project`,
			{
				// name: 'New Project',
				// description: 'New Project Description',
				workspaceId: activeWorkspace?.id
			},
			{
				headers: {
					Authorization: `Bearer ${await getAccessToken()}`
				}
			}
		);

		Logger.log('response', response);

		setCreating(false);

		// Check if new tab
		if (e.metaKey || e.ctrlKey) {
			// Open same target in new tab
			// window.open(
			// 	`/ws/${activeWorkspace?.slug}/editor/${response.data.data.project.id}/main/latest/starting-logic-generation`,
			// 	'_blank',
			// );
			window.open(
				`/ws/${activeWorkspace?.slug}/editor/${response.data.data.project.id}/main/latest`,
				'_blank'
			);

			onCreated(response.data.data.project as IProject_meta);
			return;
		}

		// navigate(
		// 	`/ws/${activeWorkspace?.slug}/editor/${response.data.data.project.id}/main/latest/starting-logic-generation`,
		// );
		navigate(
			`/ws/${activeWorkspace?.slug}/editor/${response.data.data.project.id}/main/latest`
		);
	};

	useEffect(() => {
		// Fetch user data or perform any side effects here
		const fetchCurrentUser = async () => {
			try {
				const currentUser = await getCurrentUserOrLogoutRedirect();
				setUser(currentUser);
			} catch (e) {
				Logger.log('Error getting user profile', e);
			}
		};
		fetchCurrentUser();
	}, []);

	if (viewMode === 'rows') {
		return (
			<>
				<div
					onClick={createApp}
					className="gallery-row-card gallery-row-card--create"
					id={`gallery-card--create-new-project`}
					style={
						creating || totalProjectCount === -1
							? { opacity: 0.6, cursor: 'unset' }
							: {}
					}
				>
					<div className="gallery-row-card-left">
						<div className="gallery-row-card-image bg-color-5">
							{creating ? (
								<CircularProgress size={20} />
							) : (
								<i className="fa-solid fa-plus"></i>
							)}
						</div>
						<div
							className="gallery-row-card-info"
							style={isAtProjectLimit ? { opacity: 0.6 } : {}}
						>
							<h3 className="gallery-row-card-title all-uppercase">
								Create project
							</h3>
							<p className="gallery-row-card-description">
								{isAtProjectLimit
									? 'Upgrade your plan to create more projects'
									: 'Click here to create a new app'}
							</p>
						</div>
					</div>
					{isAtProjectLimit && (
						<div className="gallery-row-card-right">
							<Box>
								<ProPlanChip />
							</Box>
						</div>
					)}
				</div>
				<Popup
					anchorQuerySelector={`#gallery-card--create-new-project`}
					placement="bottom"
					openDelay={isAtProjectLimit ? 0 : 500}
					closeDelay={100}
				>
					{isAtProjectLimit ? (
						<div className="tooltip gallery-card-tooltip">
							<h4 style={{ margin: '0 0 4px 0' }}>
								Project limit reached
							</h4>
							<p style={{ margin: 0 }}>
								Upgrade your plan to create more projects.
							</p>
						</div>
					) : (
						<div className="tooltip gallery-card-tooltip">
							Click here to create a new app
						</div>
					)}
				</Popup>
			</>
		);
	}

	return (
		<>
			<Card
				id={`gallery-card--create-new-project`}
				onClick={createApp}
				disabled={creating || totalProjectCount === -1}
			>
				<div className="card-content">
					{isAtProjectLimit && (
						// Show an upgrade plan chip on the top right corner of the card
						// On click, navigate to the plans pricing page
						<Box className="card-tags">
							<ProPlanChip />
						</Box>
					)}
					<div
						className="card-image bg-color-5"
						style={{
							position: 'relative',
							...(isAtProjectLimit ? { opacity: 0.6 } : {})
						}}
					>
						{creating ? (
							<CircularProgress size={36} />
						) : (
							<i className="fa-solid fa-plus"></i>
						)}
					</div>
					<div
						className="card-info-section"
						style={isAtProjectLimit ? { opacity: 0.6 } : {}}
					>
						<div className="card-info-section--left">
							<h3 className="card-title truncated-title all-uppercase">
								Create project
							</h3>
							<p className="card-description">
								{isAtProjectLimit
									? 'Upgrade your plan to create more projects'
									: 'Click here to create a new app'}
							</p>
						</div>
					</div>
				</div>
			</Card>
			<Popup
				anchorQuerySelector={`#gallery-card--create-new-project`}
				placement="bottom"
				openDelay={isAtProjectLimit ? 0 : 500}
				closeDelay={100}
			>
				{isAtProjectLimit ? (
					<div className="tooltip gallery-card-tooltip">
						<h4 style={{ margin: '0 0 4px 0' }}>
							Project limit reached
						</h4>
						<p style={{ margin: 0 }}>
							Upgrade your plan to create more projects.
						</p>
					</div>
				) : (
					<div className="tooltip gallery-card-tooltip">
						Click here to create a new app
					</div>
				)}
			</Popup>
		</>
	);
};

export default CreateNewProject;
